import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { getMyBillingPermissions } from "./billing-api";

// ─────────────────────────────────────────────────────────────────────────────
// IPD Billing permission context
//
// Thin wrapper over GET /ipd/billing/permissions/ (canonical codes + role level).
// Loaded once when the billing shell mounts, cached in sessionStorage so a reload
// doesn't flash-hide every gated button before the fetch resolves.
//
// Usage:
//   const { can, loading } = useBillingPermissions();
//   {can(BILLING_PERMS.RECEIPT_CANCEL_EXECUTE) && <button>Request Cancel</button>}
//
// Rule (spec Part B): HIDE what the user lacks — never render a disabled
// approve/execute button for a user without that permission.
// ─────────────────────────────────────────────────────────────────────────────

const CACHE_KEY = "ipd_billing_permissions";
// Legacy test-role key — cleared on load so old sessions don't keep overriding
// real permissions after the role switcher was removed.
const LEGACY_ROLE_KEY = "ipd_billing_test_role";

const BillingPermissionContext = createContext({
  codes: [],
  can: () => true, // fail open by default (no enforcement until API responds)
  canAny: () => true,
  roleLevel: null,
  meetsLevel: () => true,
  loaded: false,
  loading: true,
  error: null,
  refresh: () => {},
});

// Identifies the logged-in user so cached permissions can't leak across a user
// switch in the same tab (e.g. Supervisor → Cashier).
function currentUser() {
  try {
    return sessionStorage.getItem("userName") || sessionStorage.getItem("username") || "";
  } catch {
    return "";
  }
}

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    // Only trust a cache we can confirm belongs to the current user. A cache from a
    // different (or unknown) user is ignored — we re-fetch and stay fail-closed on
    // gated items until the real list arrives, so a Cashier can't inherit a
    // Supervisor's approve permissions.
    if (Array.isArray(parsed?.codes) && parsed.user && parsed.user === currentUser()) {
      return { codes: parsed.codes, roleLevel: parsed.roleLevel ?? null, loaded: true };
    }
  } catch {
    /* ignore */
  }
  return { codes: [], roleLevel: null, loaded: false };
}

export function BillingPermissionsProvider({ children }) {
  const initial = readCache();
  const [codes, setCodes] = useState(initial.codes);
  const [roleLevel, setRoleLevel] = useState(initial.roleLevel);
  // `loaded` = the permissions endpoint answered successfully at least once.
  // Until then we FAIL OPEN so the UI stays usable; the moment a real permission
  // list arrives, gating switches on automatically.
  const [loaded, setLoaded] = useState(initial.loaded);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // One-time cleanup of the removed role-switcher override.
  useEffect(() => {
    try { sessionStorage.removeItem(LEGACY_ROLE_KEY); } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { codes: list, roleLevel: level } = await getMyBillingPermissions();
      setCodes(list);
      setRoleLevel(level);
      setLoaded(true);
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ codes: list, roleLevel: level, loaded: true, user: currentUser() }));
      } catch {
        /* storage full / disabled — ignore */
      }
    } catch (err) {
      setError(err);
      // Transient failure — keep the cached codes, stay fail-open if never loaded.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const codeSet = useMemo(() => new Set(codes), [codes]);

  // Fail open until we have a definitive permission list from the server.
  const can = useCallback(
    (code) => (loaded ? codeSet.has(code) : true),
    [codeSet, loaded]
  );
  const canAny = useCallback(
    (...list) => (loaded ? list.flat().some((c) => codeSet.has(c)) : true),
    [codeSet, loaded]
  );
  // True when the current user's level meets a required minimum. If either level
  // is unknown, don't block (defer to server enforcement).
  const meetsLevel = useCallback(
    (minLevel) => {
      if (minLevel == null || roleLevel == null) return true;
      return Number(roleLevel) >= Number(minLevel);
    },
    [roleLevel]
  );

  const value = useMemo(
    () => ({ codes, can, canAny, roleLevel, meetsLevel, loaded, loading, error, refresh: load }),
    [codes, can, canAny, roleLevel, meetsLevel, loaded, loading, error, load]
  );

  return (
    <BillingPermissionContext.Provider value={value}>
      {children}
    </BillingPermissionContext.Provider>
  );
}

export function useBillingPermissions() {
  return useContext(BillingPermissionContext);
}
